import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api, API_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { statusClass, toDateTime, LOSS_REASONS, lossReasonLabel } from "../lib/helpers";
import { log } from "../lib/logger";
import { Search, Plus, Download, Upload, Camera, MapPin, MessageCircle, Archive } from "lucide-react";
import LeadFormDialog from "../components/LeadFormDialog";
import WhatsAppShareDialog from "../components/WhatsAppShareDialog";
import LostCleanupDialog from "../components/LostCleanupDialog";

export default function Leads() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [assignedFilter, setAssignedFilter] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [showForm, setShowForm] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [savedViews, setSavedViews] = useState([]);
  const [importing, setImporting] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);
  // Sub-tab: "active" (NEW+CONTACTED+IN_PROGRESS) | "won" | "lost". Default active.
  const [subTab, setSubTab] = useState(() => localStorage.getItem("sohom_leads_subtab") || "active");
  const [counts, setCounts] = useState({ ACTIVE: 0, WON: 0, LOST: 0, TOTAL: 0 });
  const [lossReasonFilter, setLossReasonFilter] = useState("ALL");
  const [waLead, setWaLead] = useState(null);
  const [showCleanup, setShowCleanup] = useState(false);
  // Render windowing — only paint this many rows into the DOM at once. Grows on
  // scroll so filtering/searching still runs across the full (light) dataset,
  // but we never mount thousands of table rows at once.
  const PAGE = 50;
  const [renderLimit, setRenderLimit] = useState(PAGE);

  const subTabStatuses = useMemo(() => ({
    active: "NEW,CONTACTED,IN_PROGRESS",
    won: "WON",
    lost: "LOST",
  }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const statusParam = subTabStatuses[subTab];
      const lossParam = subTab === "lost" && lossReasonFilter !== "ALL" ? `&loss_reasons=${encodeURIComponent(lossReasonFilter)}` : "";
      const [a, b, c] = await Promise.all([
        api.get(`/leads?light=1&statuses=${encodeURIComponent(statusParam)}${lossParam}`),
        api.get("/users"),
        api.get("/leads/status-counts"),
      ]);
      if (cancelled) return;
      setLeads(a.data);
      setUsers(b.data);
      setCounts(c.data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reloadTick, subTab, subTabStatuses, lossReasonFilter]);

  // Persist the sub-tab selection across reloads
  useEffect(() => { localStorage.setItem("sohom_leads_subtab", subTab); }, [subTab]);

  // When sub-tab changes, drop the redundant client-side status filter (it's now server-side)
  useEffect(() => { setStatusFilter("ALL"); setLossReasonFilter("ALL"); }, [subTab]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sv = localStorage.getItem("sohom_views_" + user?.id);
        if (!cancelled && sv) setSavedViews(JSON.parse(sv));
      } catch (parseErr) { log.warn("[Sohom] saved views JSON is corrupt, ignoring:", parseErr); }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const userById = useMemo(() => {
    const map = {};
    for (const u of users) { map[u.id] = u; }
    return map;
  }, [users]);

  const filtered = useMemo(() => {
    let arr = leads;
    if (q.trim()) {
      const t = q.toLowerCase();
      arr = arr.filter(
        (l) =>
          (l.lead_no || "").toLowerCase().includes(t) ||
          (l.building_name || "").toLowerCase().includes(t) ||
          (l.address || "").toLowerCase().includes(t) ||
          (l.city || "").toLowerCase().includes(t) ||
          (l.locality || "").toLowerCase().includes(t)
      );
    }
    if (statusFilter !== "ALL") arr = arr.filter((l) => (l.status || "NEW").toUpperCase() === statusFilter);
    if (assignedFilter !== "ALL") {
      if (assignedFilter === "UNASSIGNED") arr = arr.filter((l) => !l.assigned_to);
      else arr = arr.filter((l) => l.assigned_to === assignedFilter);
    }
    if (dateFrom) arr = arr.filter((l) => new Date(l.created_at) >= new Date(dateFrom));
    if (dateTo) arr = arr.filter((l) => new Date(l.created_at) <= new Date(dateTo + "T23:59:59"));
    const dir = sortDir === "asc" ? 1 : -1;
    arr = [...arr].sort((a, b) => {
      const av = a[sortBy] ?? "";
      const bv = b[sortBy] ?? "";
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return arr;
  }, [leads, q, statusFilter, assignedFilter, dateFrom, dateTo, sortBy, sortDir]);

  // Reset the render window whenever the filtered result changes.
  useEffect(() => { setRenderLimit(PAGE); }, [q, statusFilter, assignedFilter, dateFrom, dateTo, sortBy, sortDir, subTab, lossReasonFilter, leads]);

  const visibleLeads = useMemo(() => filtered.slice(0, renderLimit), [filtered, renderLimit]);

  // Grow the window when the sentinel scrolls into view (infinite scroll).
  const sentinelRef = useCallback((node) => {
    if (!node) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setRenderLimit((n) => n + PAGE);
    }, { rootMargin: "400px" });
    obs.observe(node);
  }, []);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir("desc"); }
  };

  const onExport = async () => {
    const r = await api.get("/leads/export.xlsx", { responseType: "blob" });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "leads.xlsx"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const onDownloadTemplate = async () => {
    const r = await api.get("/leads/import-template.xlsx", { responseType: "blob" });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "leads_import_template.xlsx"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Imported ${r.data.created} leads. ${r.data.errors?.length ? r.data.errors.length + " errors." : ""}`);
      reload();
    } catch (ex) {
      alert("Import failed: " + (ex?.response?.data?.detail || ex.message));
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const onExportPDF = () => {
    // Build the print document via DOM APIs (no document.write → no XSS risk)
    const win = window.open("", "_blank");
    if (!win) return;
    const doc = win.document;
    doc.title = "Sohom Lead Ops — Leads Report";
    const style = doc.createElement("style");
    style.textContent =
      "body{font-family: 'Plus Jakarta Sans', sans-serif; color:#0f172a; padding:24px;}" +
      "h1{font-family:'Outfit',sans-serif;color:#0284C7;}" +
      "table{width:100%;border-collapse:collapse;font-size:11px;}" +
      "th,td{padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:left;}" +
      "th{background:#f1f5f9;text-transform:uppercase;font-size:10px;letter-spacing:0.04em;}";
    doc.head.appendChild(style);
    const h1 = doc.createElement("h1");
    h1.textContent = "Sohom Lead Ops — Leads Report";
    const meta = doc.createElement("div");
    meta.style.cssText = "font-size:12px;color:#475569;margin-bottom:16px;";
    meta.textContent = `Generated ${new Date().toLocaleString()} · ${filtered.length} leads`;
    const table = doc.createElement("table");
    const thead = doc.createElement("thead");
    const trh = doc.createElement("tr");
    ["Lead#", "Building", "City", "Locality", "Units", "Status", "Created By", "Created At"].forEach((h) => {
      const th = doc.createElement("th");
      th.textContent = h;
      trh.appendChild(th);
    });
    thead.appendChild(trh);
    table.appendChild(thead);
    const tbody = doc.createElement("tbody");
    filtered.forEach((l) => {
      const tr = doc.createElement("tr");
      [l.lead_no, l.building_name || "Unnamed", l.city || "", l.locality || "",
       String(l.estimated_units || 0), l.status || "NEW", l.created_by_name || "",
       toDateTime(l.created_at)].forEach((cell) => {
        const td = doc.createElement("td");
        td.textContent = cell;
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    doc.body.appendChild(h1);
    doc.body.appendChild(meta);
    doc.body.appendChild(table);
    setTimeout(() => win.print(), 300);
  };

  const onSaveView = () => {
    const name = prompt("Save current filters as view (name):");
    if (!name) return;
    const view = { name, q, statusFilter, assignedFilter, dateFrom, dateTo, sortBy, sortDir };
    const arr = [...savedViews.filter((v) => v.name !== name), view];
    setSavedViews(arr);
    localStorage.setItem("sohom_views_" + user.id, JSON.stringify(arr));
  };

  const onApplyView = (v) => {
    setQ(v.q); setStatusFilter(v.statusFilter); setAssignedFilter(v.assignedFilter);
    setDateFrom(v.dateFrom); setDateTo(v.dateTo); setSortBy(v.sortBy); setSortDir(v.sortDir);
  };

  return (
    <Shell>
      <div className="px-4 sm:px-8 py-5 sm:py-7 max-w-[1400px]">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>Leads</h1>
            <p className="text-sm text-slate-500 mt-1">{filtered.length} of {leads.length} loaded · {counts.TOTAL} total across all statuses</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {user?.role === "admin" && (
              <>
                <button onClick={onDownloadTemplate} className="sohom-btn-secondary" data-testid="download-template-btn">
                  <Download className="w-4 h-4" /> Template
                </button>
                <label className="sohom-btn-secondary cursor-pointer" data-testid="import-leads-btn">
                  <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Import"}
                  <input type="file" accept=".xlsx,.xls" hidden onChange={onImportFile} />
                </label>
              </>
            )}
            <button onClick={onExport} className="sohom-btn-secondary" data-testid="export-xlsx-btn">
              <Download className="w-4 h-4" /> Excel
            </button>
            <button onClick={onExportPDF} className="sohom-btn-secondary" data-testid="export-pdf-btn">
              <Download className="w-4 h-4" /> PDF
            </button>
            {user?.role === "admin" && subTab === "lost" && (
              <button onClick={() => setShowCleanup(true)} className="sohom-btn-secondary text-red-600 border-red-200 hover:bg-red-50" data-testid="lost-cleanup-btn">
                <Archive className="w-4 h-4" /> Export &amp; clean up
              </button>
            )}
            <button onClick={() => { setEditLead(null); setShowForm(true); }} className="sohom-btn-primary" data-testid="new-lead-btn">
              <Plus className="w-4 h-4" /> New Lead
            </button>
          </div>
        </div>

        {/* Sub-tabs — active / won / lost. Fetches only the selected group from the backend. */}
        <div className="flex gap-1 mb-4 border-b border-slate-200" data-testid="leads-subtabs">
          {[
            { key: "active", label: "Active", desc: "New · Contacted · In-progress", count: counts.ACTIVE },
            { key: "won", label: "Won", count: counts.WON },
            { key: "lost", label: "Lost", count: counts.LOST },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setSubTab(t.key)}
              data-testid={`leads-subtab-${t.key}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                subTab === t.key ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {t.label}
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${
                subTab === t.key ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-500"
              }`}>{t.count}</span>
              {t.desc && subTab === t.key ? <span className="hidden sm:inline text-[11px] text-slate-400 font-normal ml-1">({t.desc})</span> : null}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="sohom-card p-4 mb-4 space-y-3">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="sohom-input pl-9"
                placeholder="Search lead #, building, address, city…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="leads-search"
              />
            </div>
            <select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)} className="sohom-input w-44" data-testid="leads-assigned-filter">
              <option value="ALL">All Assignees</option>
              <option value="UNASSIGNED">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="sohom-input w-44" data-testid="leads-date-from" />
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="sohom-input w-44" data-testid="leads-date-to" />
            <button onClick={onSaveView} className="sohom-btn-secondary" data-testid="save-view-btn">Save view</button>
          </div>
          {subTab === "active" && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold mr-2">Narrow by status:</span>
              {["ALL", "NEW", "CONTACTED", "IN_PROGRESS"].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  data-testid={`status-chip-${s}`}
                  className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${
                    statusFilter === s
                      ? "bg-sky-600 text-white border-sky-600"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
          )}
          {subTab === "lost" && (
            <div className="flex flex-wrap gap-1.5 items-center" data-testid="loss-reason-filter-row">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold mr-2">Loss reason:</span>
              {[{ value: "ALL", label: "All" }, ...LOSS_REASONS].map((r) => (
                <button
                  key={r.value}
                  onClick={() => setLossReasonFilter(r.value)}
                  data-testid={`loss-reason-chip-${r.value}`}
                  className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${
                    lossReasonFilter === r.value
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}
          {savedViews.length > 0 && (
            <div className="flex gap-1.5 items-center">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Views:</span>
              {savedViews.map((v) => (
                <button key={v.name} onClick={() => onApplyView(v)} className="px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded">{v.name}</button>
              ))}
            </div>
          )}
        </div>

        {/* Table (desktop) */}
        <div className="hidden md:block sohom-card overflow-hidden" data-testid="leads-table">
          <div className="overflow-x-auto">
            <table className="sohom-table">
              <thead>
                <tr>
                  {[
                    { k: "lead_no", l: "Lead #" },
                    { k: "building_name", l: "Building" },
                    { k: "locality", l: "Area" },
                    { k: "city", l: "City" },
                    { k: "estimated_units", l: "Units" },
                    { k: "status", l: "Status" },
                    { k: "assigned_to", l: "Assigned" },
                    { k: "created_by_name", l: "Created By" },
                    { k: "created_at", l: "Created At" },
                  ].map(({ k, l }) => (
                    <th key={k} onClick={() => toggleSort(k)} className="cursor-pointer select-none">
                      {l} {sortBy === k && (sortDir === "asc" ? "↑" : "↓")}
                    </th>
                  ))}
                  <th>GPS</th>
                  <th>Photos</th>
                  <th className="text-right pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  if (loading) return <tr><td colSpan={12} className="text-center text-slate-400 py-10">Loading…</td></tr>;
                  if (filtered.length === 0) return <tr><td colSpan={12} className="text-center text-slate-400 py-10">No leads match filters.</td></tr>;
                  return visibleLeads.map((l) => (
                  <tr key={l.id} onClick={() => navigate(`/leads/${l.id}`)} data-testid={`lead-row-${l.lead_no}`}>
                    <td className="font-mono text-slate-700">{l.lead_no}</td>
                    <td className="font-medium text-slate-900">{l.building_name || "Unnamed"}</td>
                    <td>{l.locality || "—"}</td>
                    <td>{l.city || "—"}</td>
                    <td>{l.estimated_units || 0}</td>
                    <td>
                      <span className={statusClass(l.status)}>{(l.status || "NEW").replace("_", " ")}</span>
                      {l.status === "LOST" && l.loss_reason && (
                        <div className="text-[10px] text-red-600 mt-0.5" data-testid={`lost-reason-cell-${l.lead_no}`}>{lossReasonLabel(l.loss_reason)}</div>
                      )}
                    </td>
                    <td>{userById[l.assigned_to]?.full_name || <span className="text-slate-400 italic">Unassigned</span>}</td>
                    <td>{l.created_by_name || "—"}</td>
                    <td className="text-slate-500">{toDateTime(l.created_at)}</td>
                    <td>{l.latitude ? <MapPin className="w-3.5 h-3.5 text-sky-600 inline" /> : <span className="text-slate-300">—</span>}</td>
                    <td><span className="inline-flex items-center gap-1 text-slate-600"><Camera className="w-3.5 h-3.5" />{l.photo_count ?? l.photos?.length ?? 0}</span></td>
                    <td className="text-right pr-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); setWaLead(l); }}
                        className="p-1.5 rounded text-emerald-600 hover:bg-emerald-50"
                        title="Send WhatsApp intro"
                        aria-label="Send WhatsApp intro"
                        data-testid={`wa-row-btn-${l.lead_no}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>

        {/* Cards (mobile) */}
        <div className="md:hidden space-y-3" data-testid="leads-cards">
          {loading ? (
            <div className="text-center text-slate-400 py-10">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-slate-400 py-10 sohom-card">No leads match filters.</div>
          ) : visibleLeads.map((l) => (
            <div key={l.id} className="relative w-full sohom-card p-4">
              <button
                onClick={(e) => { e.stopPropagation(); setWaLead(l); }}
                className="absolute top-3 right-3 p-2 rounded-full text-emerald-600 hover:bg-emerald-50"
                title="Send WhatsApp intro"
                aria-label="Send WhatsApp intro"
                data-testid={`wa-card-btn-${l.lead_no}`}
              >
                <MessageCircle className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate(`/leads/${l.id}`)}
                data-testid={`lead-card-${l.lead_no}`}
                className="w-full text-left active:bg-slate-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3 mb-1.5 pr-9">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[11px] text-slate-500">{l.lead_no}</div>
                    <div className="font-semibold text-slate-900 text-base truncate">{l.building_name || "Unnamed"}</div>
                  </div>
                  <span className={statusClass(l.status)}>{(l.status || "NEW").replace("_", " ")}</span>
                </div>
                <div className="text-sm text-slate-600 mb-2 truncate">
                  {[l.locality, l.city].filter(Boolean).join(" · ") || "No location"}
                </div>
                {l.status === "LOST" && l.loss_reason && (
                  <div className="text-xs text-red-600 mb-2" data-testid={`lost-reason-card-${l.lead_no}`}>
                    Lost — {lossReasonLabel(l.loss_reason)}
                  </div>
                )}
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><Camera className="w-3 h-3" />{l.photo_count ?? l.photos?.length ?? 0}</span>
                  {l.latitude ? <span className="inline-flex items-center gap-1 text-sky-600"><MapPin className="w-3 h-3" />GPS</span> : null}
                  <span>{l.estimated_units || 0} units</span>
                  <span className="ml-auto">{toDateTime(l.created_at).split(" ")[0]}</span>
                </div>
                {l.assigned_to && userById[l.assigned_to] && (
                  <div className="text-xs text-slate-500 mt-1.5">→ {userById[l.assigned_to].full_name}</div>
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Infinite-scroll sentinel + count footer */}
        {!loading && filtered.length > 0 && (
          <div className="mt-4" data-testid="leads-render-footer">
            {visibleLeads.length < filtered.length ? (
              <>
                <div ref={sentinelRef} aria-hidden className="h-1" />
                <div className="flex justify-center py-3">
                  <button
                    onClick={() => setRenderLimit((n) => n + PAGE)}
                    className="sohom-btn-secondary"
                    data-testid="load-more-btn"
                  >
                    Load more ({filtered.length - visibleLeads.length} more)
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center text-xs text-slate-400 py-3">Showing all {filtered.length} leads.</div>
            )}
          </div>
        )}
      </div>

      {showForm && (
        <LeadFormDialog
          users={users}
          initial={editLead}
          onClose={() => { setShowForm(false); setEditLead(null); }}
          onSaved={() => { setShowForm(false); setEditLead(null); reload(); }}
        />
      )}
      {waLead && (
        <WhatsAppShareDialog lead={waLead} onClose={() => setWaLead(null)} />
      )}
      {showCleanup && (
        <LostCleanupDialog
          lostLeads={leads}
          onClose={() => setShowCleanup(false)}
          onDeleted={(n) => { setShowCleanup(false); alert(`Deleted ${n} lost lead(s) and freed their storage.`); reload(); }}
        />
      )}
    </Shell>
  );
}
